import g from '#core/global';
import load from '#core/load';
import layout from '#core/layout';
import events from '#core/events';
import pattern_config from '#core/pattern-config';


const scroll_lock_$ = '.scroll-lock';
const scroll_$ = '.scroll-lock__scroll';
const chunks_$ = '.scroll-lock__scroll__chunks';
const chunk_$ = '.scroll-lock__chunk';
const dots_$ = '.scroll-lock__dots';
const dot_$ = '.scroll-lock__dot';

const active_attr = 'data-active';
const dot_index_attr = 'data-scroll-lock-dot';

const layout_switch = pattern_config.scroll_lock.layout_switch;

let has_scroll_lock = false;
let instances = [];


const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('scroll-lock')) return false;

		load.content(() => {
			has_scroll_lock = assign_elements();
			if (!has_scroll_lock) return false;
		});
		load.all(() => {
			has_scroll_lock = assign_elements();
			if (!has_scroll_lock) return false;

			assign_global_events();
			instances.forEach(update_instance);
		});
	},

};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_scroll_lock) return true;

	const $scroll_locks = document.querySelectorAll(scroll_lock_$);
	if (!$scroll_locks.length) return false;

	instances = [];
	$scroll_locks.forEach($scroll_lock => {
		const instance = build_instance($scroll_lock);
		if (!instance) return;
		instances.push(instance);
		assign_instance_events(instance);
	});

	return instances.length > 0;
}


/**
 * Build instance state for a scroll-lock element
 * @param {element} $scroll_lock
 * @return {object|null}
 */
function build_instance($scroll_lock) {
	const $scroll = $scroll_lock.querySelector(scroll_$);
	const $chunks_wrap = $scroll_lock.querySelector(chunks_$);
	const $chunks = [...$scroll_lock.querySelectorAll(chunk_$)];
	const $dots = [...$scroll_lock.querySelectorAll(dot_$)];
	if (!$chunks.length) return null;

	return {
		$scroll_lock,
		$scroll,
		$chunks_wrap,
		$chunks,
		$dots,
		active_index: -1,
		raf_pending: false,
	};
}


/**
 * Assign per-instance event listeners
 * @param {object} instance
 */
function assign_instance_events(instance) {
	instance.$dots.forEach($dot => {
		$dot.addEventListener(g.click_e, () => {
			const index = parseInt($dot.getAttribute(dot_index_attr), 10);
			scroll_to_chunk(instance, index);
		});
	});

	instance.$chunks_wrap.addEventListener(g.scroll_e, () => {
		schedule_update(instance);
	});
}


/**
 * Assign global event listeners
 */
function assign_global_events() {
	window.addEventListener(g.scroll_e, () => {
		instances.forEach(schedule_update);
	}, { passive: true });

	window.addEventListener(g.resize_e, () => {
		instances.forEach(schedule_update);
	});

	document.addEventListener(events.layout.change, () => {
		instances.forEach(reset_instance);
	});
}


/**
 * rAF-coalesced update for an instance
 * @param {object} instance
 */
function schedule_update(instance) {
	if (instance.raf_pending) return;
	instance.raf_pending = true;
	requestAnimationFrame(() => {
		instance.raf_pending = false;
		update_instance(instance);
	});
}


/**
 * Update active index, then chunk + dot active states
 * @param {object} instance
 */
function update_instance(instance) {
	const is_desktop = layout.isBreakpointMin(layout_switch);
	const new_active_index = (is_desktop)
		? update_desktop(instance)
		: update_mobile(instance);

	set_active_chunk(instance, new_active_index);
	set_active_dot(instance, new_active_index);
	dispatch_change(instance, new_active_index);
}


/**
 * Toggle the active state on chunks (desktop fade is driven by this)
 * @param {object} instance
 * @param {number} index
 */
function set_active_chunk(instance, index) {
	instance.$chunks.forEach(($chunk, chunk_index) => {
		if (chunk_index === index) {
			$chunk.setAttribute(active_attr, '');
		} else {
			$chunk.removeAttribute(active_attr);
		}
	});
}


/**
 * Pick active chunk by vertical distance from viewport center (desktop)
 * @param {object} instance
 * @return {number}
 */
function update_desktop(instance) {
	const viewport_center = window.innerHeight / 2;

	let best_index = 0;
	let best_distance = Infinity;

	instance.$chunks.forEach(($chunk, index) => {
		const bounds = $chunk.getBoundingClientRect();
		const chunk_center = bounds.top + (bounds.height / 2);
		const distance = Math.abs(chunk_center - viewport_center);
		if (distance < best_distance) {
			best_distance = distance;
			best_index = index;
		}
	});

	return best_index;
}


/**
 * Pick active chunk by horizontal distance from container center (mobile)
 * @param {object} instance
 * @return {number}
 */
function update_mobile(instance) {
	const $wrap = instance.$chunks_wrap;
	if (!$wrap) return 0;

	const wrap_bounds = $wrap.getBoundingClientRect();
	const wrap_center = wrap_bounds.left + (wrap_bounds.width / 2);

	let best_index = 0;
	let best_distance = Infinity;

	instance.$chunks.forEach(($chunk, index) => {
		const bounds = $chunk.getBoundingClientRect();
		const chunk_center = bounds.left + (bounds.width / 2);
		const distance = Math.abs(chunk_center - wrap_center);
		if (distance < best_distance) {
			best_distance = distance;
			best_index = index;
		}
	});

	return best_index;
}


/**
 * Reset state when layout breakpoint changes
 * @param {object} instance
 */
function reset_instance(instance) {
	instance.active_index = -1;
	schedule_update(instance);
}


/**
 * Toggle the active state on dots
 * @param {object} instance
 * @param {number} index
 */
function set_active_dot(instance, index) {
	instance.$dots.forEach(($dot, dot_index) => {
		if (dot_index === index) {
			$dot.setAttribute(active_attr, '');
		} else {
			$dot.removeAttribute(active_attr);
		}
	});
}


/**
 * Dispatch enter/leave events on active-index change
 * @param {object} instance
 * @param {number} new_index
 */
function dispatch_change(instance, new_index) {
	if (new_index === instance.active_index) return;

	if (instance.active_index !== -1) {
		instance.$scroll_lock.dispatchEvent(new CustomEvent(events.scroll_lock.leave, {
			bubbles: true,
			detail: { index: instance.active_index },
		}));
	}

	instance.active_index = new_index;

	instance.$scroll_lock.dispatchEvent(new CustomEvent(events.scroll_lock.enter, {
		bubbles: true,
		detail: { index: new_index },
	}));
}


/**
 * Scroll to a chunk by index (mode-aware)
 * @param {object} instance
 * @param {number} index
 */
function scroll_to_chunk(instance, index) {
	const $chunk = instance.$chunks[index];
	if (!$chunk) return;

	if (layout.isBreakpointMin(layout_switch)) {
		const bounds = $chunk.getBoundingClientRect();
		const chunk_center = bounds.top + (bounds.height / 2);
		const offset = chunk_center - (window.innerHeight / 2);
		window.scrollTo({
			top: window.scrollY + offset,
			behavior: 'smooth',
		});
	} else {
		$chunk.scrollIntoView({
			behavior: 'smooth',
			inline: 'center',
			block: 'nearest',
		});
	}
}
